import type { FileDto, FileVariantDto } from '@/api/api.dto';

/** Image renditions the services produce on upload, all webp. */
export const IMAGE_VARIANT = {
  /** 192×192, centre-cropped square. */
  THUMB: 'thumb',
  /** Fits within 640×640, aspect kept. */
  SMALL: 'small',
  /** Fits within 1280×1280, aspect kept. */
  MEDIUM: 'medium',
} as const;

export type ImageVariant = (typeof IMAGE_VARIANT)[keyof typeof IMAGE_VARIANT];

const IMAGE_VARIANTS: readonly ImageVariant[] = Object.values(IMAGE_VARIANT);

const MAX_PIXEL_RATIO = 3;

/** Where a variant is served when the record does not spell its link out. */
export const fileVariantLink = (file: FileDto, name: string, variant?: FileVariantDto): string =>
  variant?.link || `${file.link.split('?')[0]}/variants/${name}`;

/**
 * The smallest image rendition that still covers a `width`×`height` CSS-px box (drawn with
 * `object-fit: cover`) at the device's pixel ratio. `thumb` is a crop, so it only serves
 * square boxes. Without a fitting variant the original — the largest there is — is used.
 */
export const imageVariantLink = (
  file: FileDto | null | undefined,
  width: number,
  height = width,
): string | null => {
  if (!file) return null;

  const ratio = Math.min(
    (typeof window !== 'undefined' && window.devicePixelRatio) || 1,
    MAX_PIXEL_RATIO,
  );
  const neededW = Math.ceil(width * ratio);
  const neededH = Math.ceil(height * ratio);

  let best: { link: string; area: number } | null = null;
  for (const name of IMAGE_VARIANTS) {
    const variant = file.variants?.[name];
    if (!variant?.width || !variant.height) continue;
    if (name === IMAGE_VARIANT.THUMB && width !== height) continue;
    if (variant.width < neededW || variant.height < neededH) continue;

    const area = variant.width * variant.height;
    if (!best || area < best.area) best = { link: fileVariantLink(file, name, variant), area };
  }

  return best?.link ?? file.link;
};
